import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const TeamContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const TeamImage = styled.div`
  aspect-ratio: 3/4;
  background: ${props => props.image ? `url(${props.image}) center/cover` : props.theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
`;

const TeamContent = styled.div`
  padding: 2rem;
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 0.5rem;
  }
  
  .role {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  .skills {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: inline-block;
      background: ${props => props.theme.colors.backgroundLight};
      color: ${props => props.theme.colors.textLight};
      padding: 0.25rem 0.75rem;
      margin: 0.25rem;
      border-radius: 15px;
      font-size: 0.9rem;
    }
  }
`;

const Team = () => {
  const teamMembers = [
    {
      id: 'marcella',
      name: 'Marcella Deeken',
      image: '/assets/Marcella.jpg'
    },
    {
      id: 'denise',
      name: 'Denise Fecht',
      image: '/assets/Denise.jpg'
    },
    {
      id: 'martina',
      name: 'Martina Swart',
      image: '/assets/Martina.jpg'
    },
    {
      id: 'kerstin',
      name: 'Kerstin Krallmann',
      image: '/assets/Kerstin.jpg'
    }
  ];

  return (
    <TeamContainer>
      <Helmet>
        <title>Unser Team - Der Ponystall | Marcella Deeken und Team</title>
        <meta name="description" content="Lernen Sie unser erfahrenes Team kennen. Marcella Deeken und ihre Mitarbeiter sorgen für professionelle tiergestützte Pädagogik." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Unser Team
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Menschen mit Herz für Ponys</h2>
            <p className="section-subtitle">
              Unser erfahrenes und liebevolles Team sorgt dafür, dass sich bei uns 
              alle wohlfühlen - Menschen und Ponys gleichermaßen.
            </p>
          </div>

          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamCard
                key={member.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <TeamImage image={member.image}>
                  {member.emoji && member.emoji}
                </TeamImage>
                <TeamContent>
                  <h3>{member.name}</h3>
                </TeamContent>
              </TeamCard>
            ))}
          </TeamGrid>
        </Container>
      </ContentSection>
    </TeamContainer>
  );
};

export default Team;